import { Line } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateLineArgs,
    getDefaultLineCreateArgs,
    UpdateLineArgs,
} from "@nova/serializer/src/client/validators/cubes/line"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { setIdRenaming } from "features/data-management/workspace/WorkspaceManagementSlice"
import { GeometryType } from "@nova/constants"
import { selectSingleLayer } from "panels/content/side-panel/slice.new"
import { maybeSetProjectLocation, centroid } from "../projectLocation"

const {
    create: createLine,
    update: updateLine,
    delete: deleteLine,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    Line, // entity type
    CreateLineArgs, // create args to send to server
    [
        "coordinates",
        "geometryType",
        "width",
        "color",
        "outline",
        "fill",
        "stroke",
        "parentId",
        "holes",
        "createdBy",
    ], // create args required by action
    UpdateLineArgs, // update args,
    { name?: string; index?: string },
    { index?: string }
>("Line")

export const addLinesToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            const lines = state.project.data.lines
            const name =
                context?.name ||
                `${data.geometryType === GeometryType.Polygon ? "Polygon" : "Line"} ${Object.values(lines).length + 1}`
            const optimisticData = {
                name,
                id: randomUUID(),
                ...getDefaultLineCreateArgs(store.getState().project.data, data.parentId),
                ...data,
            }
            if (context?.index) optimisticData.index = context.index
            createLine(
                socket,
                {
                    ...data,
                    name,
                    id: optimisticData.id,
                },
                { index: optimisticData.index }
            )
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["lines"],
                })
            )
            const project = store.getState().project.data
            const center = centroid(optimisticData.coordinates)
            maybeSetProjectLocation(store, project.orgId, project.id, center)

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))

            // Only enter renaming state if an index was not provided manually.
            // Index will be provided manually if a different geometry type is converted to a line.
            if (!context?.index) {
                // Enter layer renaming state in the Inspector Panel.
                store.dispatch(setIdRenaming(`inspector-layer-title-${optimisticData.id}`))
            }
        })
        .addCase(updateAction, (store, { payload: { id, updates } }, socket) => {
            updateLine(socket, id, updates, {})
            store.dispatch(updateEntitySocket({ updates, path: ["lines", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            deleteLine(socket, id, {})
            store.dispatch(deleteEntitySocket({ path: ["lines", { id }] }))
        })

export { createAction as createLine, updateAction as updateLine, deleteAction as deleteLine }
